<template>
  <div class="workflow-timeline">
    <div v-if="workflowStates.length > 0" class="timeline-container">
      <div 
        v-for="(state, index) in workflowStates" 
        :key="state.id"
        class="timeline-item"
        :class="getTimelineItemClass(state, index)"
      >
        <!-- Timeline connector line -->
        <div 
          v-if="index < workflowStates.length - 1"
          class="timeline-line"
          :class="getTimelineLineClass(state, index)"
        ></div>
        
        <!-- Timeline dot/icon -->
        <div class="timeline-dot" :class="getTimelineDotClass(state)">
          <v-icon 
            v-if="isStatePassed(state)"
            x-small 
            color="white"
          >
            fas fa-check
          </v-icon>
          <div 
            v-else-if="isCurrentState(state)"
            class="current-dot"
          ></div>
        </div>
        
        <!-- Timeline content -->
        <div class="timeline-content">
          <div 
            class="timeline-label"
            :class="getTimelineLabelClass(state)"
          >
            {{ state.name }}
          </div>
          <div 
            v-if="isCurrentState(state)"
            class="timeline-current-indicator"
          >
            Current Status
          </div>
        </div>
      </div>
    </div>
    
    <!-- No workflow states configured -->
    <div v-else class="no-states-message">
      <v-icon color="grey" class="mb-2">mdi-sitemap</v-icon>
      <div class="text-caption text--secondary">
        No workflow states configured
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WorkflowTimeline',
  props: {
    workflowStates: {
      type: Array,
      default: () => []
    },
    currentStateId: {
      type: [Number, String],
      default: null
    }
  },
  methods: {
    isStatePassed(state) {
      if (!this.currentStateId) return false;
      
      const currentIndex = this.workflowStates.findIndex(s => s.id === this.currentStateId);
      const stateIndex = this.workflowStates.findIndex(s => s.id === state.id);
      
      return currentIndex !== -1 && stateIndex !== -1 && stateIndex < currentIndex;
    },
    
    isCurrentState(state) {
      return this.currentStateId === state.id;
    },
    
    isFutureState(state) {
      if (!this.currentStateId) return true;
      
      const currentIndex = this.workflowStates.findIndex(s => s.id === this.currentStateId);
      const stateIndex = this.workflowStates.findIndex(s => s.id === state.id);
      
      return currentIndex !== -1 && stateIndex !== -1 && stateIndex > currentIndex;
    },
    
    getTimelineItemClass(state, index) {
      return {
        'is-passed': this.isStatePassed(state),
        'is-current': this.isCurrentState(state),
        'is-future': this.isFutureState(state),
        'is-last': index === this.workflowStates.length - 1
      };
    },
    
    getTimelineLineClass(state, index) {
      // Line should be filled if current state is passed
      const isLineFilled = this.isStatePassed(state) || this.isCurrentState(state);
      return {
        'line-filled': isLineFilled,
        'line-empty': !isLineFilled
      };
    },
    
    getTimelineDotClass(state) {
      return {
        'dot-passed': this.isStatePassed(state),
        'dot-current': this.isCurrentState(state),
        'dot-future': this.isFutureState(state)
      };
    },
    
    getTimelineLabelClass(state) {
      return {
        'label-passed': this.isStatePassed(state),
        'label-current': this.isCurrentState(state),
        'label-future': this.isFutureState(state)
      };
    }
  }
}
</script>

<style scoped>
.workflow-timeline {
  padding: 8px 0;
}

.timeline-container {
  position: relative;
  padding-left: 24px;
}

.timeline-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  min-height: 48px;
  margin-bottom: 8px;
  padding-top: 2px;
}

.timeline-item.is-last {
  margin-bottom: 0;
}

.timeline-line {
  position: absolute;
  left: -12px;
  top: 9px;
  width: 2px;
  height: calc(100% + 8px);
  transition: background-color 0.3s ease;
}

.timeline-line.line-filled {
  background-color: #4CAF50;
}

.timeline-line.line-empty {
  background-color: #e0e0e0;
}

.timeline-dot {
  position: absolute;
  left: -18px;
  top: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.timeline-dot.dot-passed {
  background-color: #4CAF50;
  border-color: #4CAF50;
}

.timeline-dot.dot-current {
  background-color: #2196F3;
  border-color: #2196F3;
  box-shadow: 0 0 0 4px rgba(33, 150, 243, 0.2);
}

.timeline-dot.dot-future {
  background-color: white;
  border-color: #e0e0e0;
}

.current-dot {
  width: 6px;
  height: 6px;
  background-color: white;
  border-radius: 50%;
}

.timeline-content {
  flex: 1;
  padding-left: 8px;
  padding-top: 0;
}

.timeline-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 2px;
  transition: color 0.3s ease;
}

.timeline-label.label-passed {
  color: #4CAF50;
}

.timeline-label.label-current {
  color: #2196F3;
  font-weight: 600;
}

.timeline-label.label-future {
  color: #9e9e9e;
}

.timeline-current-indicator {
  font-size: 11px;
  color: #2196F3;
  font-weight: 500;
  font-style: italic;
}

.no-states-message {
  text-align: center;
  padding: 16px;
  color: #9e9e9e;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .timeline-container {
    padding-left: 20px;
  }
  
  .timeline-item {
    padding-top: 2px;
  }
  
  .timeline-dot {
    left: -16px;
    top: 2px;
    width: 12px;
    height: 12px;
  }
  
  .timeline-line {
    left: -10px;
    top: 8px;
  }
  
  .timeline-label {
    font-size: 13px;
  }
  
  .timeline-current-indicator {
    font-size: 10px;
  }
}
</style>
